/**
 * ============================================================================
 * SETTINGS PAGE - Dashboard-style layout (green/black, compact, same as Dashboard)
 * ============================================================================
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useUserStore } from '../store';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import Switch from '../components/common/Switch';
import Select from '../components/common/Select';
import Spinner from '../components/common/Spinner';
import '../styles/landing.css';
import '../styles/animations.css';
import '../styles/global.css';

const Settings = () => {
  const navigate = useNavigate();
  const { preferences, updatePreference: storeUpdatePreference, loading: storeLoading } = useUserStore();
  const { loadSettings, updateSettings, loading } = useUser();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();
  const [isLoading, setIsLoading] = useState(true);
  const statsInView = true;
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await loadSettings();
      } catch (error) {
        showError('Failed to load settings');
        console.error('Load settings error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [loadSettings, showError]);

  const handlePreferenceChange = async (category, key, value) => {
    try {
      storeUpdatePreference(category, key, value);
      const nextPrefs = useUserStore.getState().preferences;
      await updateSettings({ preferences: nextPrefs });
      showSuccess(language === 'fr' ? 'Préférence mise à jour!' : 'Preference updated!');
    } catch (error) {
      showError(error?.message || (language === 'fr' ? 'Échec de la mise à jour' : 'Failed to update'));
    }
  };

  if (isLoading || storeLoading) {
    return (
      <UserDashboardLayout hideFooter>
        <div className="dashboard-wt dashboard-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des paramètres...' : 'Loading settings...'}
            </p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  const settingsSections = [
    {
      id: 'notifications',
      icon: '🔔',
      title: language === 'fr' ? 'Notifications' : 'Notifications',
      description: language === 'fr' ? 'Comment recevoir les notifications' : 'How you receive notifications',
      items: [
        { type: 'email', icon: '📧', label: language === 'fr' ? 'Email' : 'Email' },
        { type: 'sms', icon: '💬', label: language === 'fr' ? 'SMS' : 'SMS' },
        { type: 'push', icon: '🔔', label: language === 'fr' ? 'Push' : 'Push' },
      ],
    },
    {
      id: 'privacy',
      icon: '🔒',
      title: language === 'fr' ? 'Confidentialité' : 'Privacy',
      description: language === 'fr' ? 'Visibilité de vos informations' : 'Who can see your information',
      items: [
        { key: 'showEmail', icon: '📧', label: language === 'fr' ? 'Afficher l\'email' : 'Show Email' },
        { key: 'showPhone', icon: '📱', label: language === 'fr' ? 'Afficher le téléphone' : 'Show Phone' },
      ],
    },
    {
      id: 'language',
      icon: '🌍',
      title: language === 'fr' ? 'Langue & Devise' : 'Language & Currency',
      description: language === 'fr' ? 'Langue et devise d\'affichage' : 'Display language and currency',
    },
    {
      id: 'security',
      icon: '🛡️',
      title: language === 'fr' ? 'Sécurité' : 'Security',
      description: language === 'fr' ? 'Mot de passe et authentification' : 'Password and authentication',
    },
  ];

  const quickActions = [
    { icon: '🔒', label: language === 'fr' ? 'Confidentialité et données' : 'Privacy & Data', path: '/settings/privacy' },
    { icon: '📥', label: language === 'fr' ? 'Exporter mes données (RGPD)' : 'Export My Data (GDPR)', path: '/settings/data-export' },
    { icon: '🆔', label: language === 'fr' ? 'Vérification du compte' : 'Account Verification', path: '/verification' },
    { icon: '🛡️', label: language === 'fr' ? 'Sécurité' : 'Security', path: '/security' },
    { icon: '🗑️', label: language === 'fr' ? 'Supprimer mon compte' : 'Delete My Account', path: '/settings/delete-account', danger: true },
  ];

  return (
    <UserDashboardLayout hideFooter>
      <div className="dashboard-wt dashboard-wt-fit-viewport">
        {/* Hero - same style as Dashboard */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />
          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              ⚙️ {language === 'fr' ? 'Paramètres' : 'Settings'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez vos préférences et paramètres de compte'
                : 'Manage your account preferences and settings'}
            </p>
          </div>
        </section>

        {/* Settings Grid - 4 cards */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-grid">
              {/* Notifications */}
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">{settingsSections[0].icon} {settingsSections[0].title}</h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <p className="dashboard-wt-card-desc">{settingsSections[0].description}</p>
                  {settingsSections[0].items.map((item) => (
                    <div key={item.type} className="settings-item-row">
                      <div className="settings-item-left">
                        <span className="settings-item-icon">{item.icon}</span>
                        <span className="settings-item-label">{item.label}</span>
                      </div>
                      <Switch
                        checked={preferences?.notifications?.[item.type] || false}
                        onChange={(checked) => handlePreferenceChange('notifications', item.type, checked)}
                        variant={preferences?.notifications?.[item.type] ? 'success' : 'default'}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Privacy */}
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">{settingsSections[1].icon} {settingsSections[1].title}</h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <p className="dashboard-wt-card-desc">{settingsSections[1].description}</p>
                  <div className="settings-select-group">
                    <label className="settings-label">{language === 'fr' ? 'Visibilité du profil' : 'Profile Visibility'}</label>
                    <Select
                      value={preferences?.privacy?.profileVisibility || 'public'}
                      onChange={(e) => handlePreferenceChange('privacy', 'profileVisibility', e.target.value)}
                      options={[
                        { value: 'public', label: language === 'fr' ? 'Public' : 'Public' },
                        { value: 'private', label: language === 'fr' ? 'Privé' : 'Private' },
                        { value: 'friends', label: language === 'fr' ? 'Amis uniquement' : 'Friends Only' },
                      ]}
                    />
                  </div>
                  {settingsSections[1].items.map((item) => (
                    <div key={item.key} className="settings-item-row">
                      <div className="settings-item-left">
                        <span className="settings-item-icon">{item.icon}</span>
                        <span className="settings-item-label">{item.label}</span>
                      </div>
                      <Switch
                        checked={preferences?.privacy?.[item.key] || false}
                        onChange={(checked) => handlePreferenceChange('privacy', item.key, checked)}
                        variant={preferences?.privacy?.[item.key] ? 'success' : 'default'}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Language & Currency */}
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">{settingsSections[2].icon} {settingsSections[2].title}</h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <p className="dashboard-wt-card-desc">{settingsSections[2].description}</p>
                  <div className="settings-select-group">
                    <label className="settings-label">{language === 'fr' ? 'Langue' : 'Language'}</label>
                    <Select
                      value={preferences?.language || 'en'}
                      onChange={(e) => handlePreferenceChange('preferences', 'language', e.target.value)}
                      options={[
                        { value: 'en', label: '🇬🇧 English' },
                        { value: 'fr', label: '🇫🇷 Français' },
                      ]}
                    />
                  </div>
                  <div className="settings-select-group">
                    <label className="settings-label">{language === 'fr' ? 'Devise' : 'Currency'}</label>
                    <Select
                      value={preferences?.currency || 'XAF'}
                      onChange={(e) => handlePreferenceChange('preferences', 'currency', e.target.value)}
                      options={[
                        { value: 'XAF', label: 'XAF - Franc CFA' },
                        { value: 'KES', label: 'KES - Kenyan Shilling' },
                        { value: 'NGN', label: 'NGN - Nigerian Naira' },
                        { value: 'GHS', label: 'GHS - Ghanaian Cedi' },
                        { value: 'ZAR', label: 'ZAR - South African Rand' },
                      ]}
                    />
                  </div>
                </div>
              </div>

              {/* Security */}
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title">{settingsSections[3].icon} {settingsSections[3].title}</h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <p className="dashboard-wt-card-desc">{settingsSections[3].description}</p>
                  <button
                    type="button"
                    className="dashboard-wt-btn-secondary"
                    onClick={() => navigate('/auth/set-password')}
                  >
                    🔐 {language === 'fr' ? 'Définir un mot de passe' : 'Set Password'}
                  </button>
                  <p className="settings-hint">
                    {language === 'fr' ? 'Optionnel: mot de passe pour vous connecter' : 'Optional: password for easier login'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions - single card with clean list */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card settings-quick-actions-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  {language === 'fr' ? 'Actions rapides' : 'Quick Actions'}
                </h2>
                <p className="dashboard-wt-card-desc">
                  {language === 'fr' ? 'Accès rapide aux paramètres importants' : 'Quick access to important settings'}
                </p>
              </div>
              <div className="settings-quick-actions-list">
                {quickActions.map((action) => (
                  <button
                    key={action.path}
                    type="button"
                    onClick={() => navigate(action.path)}
                    className={`settings-quick-action-row ${action.danger ? 'settings-quick-action-danger' : ''}`}
                  >
                    <span className="settings-quick-action-icon">{action.icon}</span>
                    <span className="settings-quick-action-label">{action.label}</span>
                    <span className="settings-quick-action-arrow">→</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </UserDashboardLayout>
  );
};

export default Settings;
